import React, { useEffect, useCallback, useState } from 'react'
import { useCache } from '@fs/zion-cache'
import { ChatInitialState, ChatReducer } from './ChatReducer'
import useRoster from '../hooks/useRoster'
import useBookmarks from '../hooks/useBookmarks'
import useChatServerConnection from '../hooks/useChatServerConnection'
import useChatServerPing from '../hooks/useChatServerPing'
import { useClearUnreadMessageFromCache, useLoadUnreadMessagesFromCache } from '../hooks/useUnreadMessageCache'
import { getIsLowBandwidth } from '../hooks/useIsLowBandwidth'
import useInterval from '../hooks/useInterval'
import { Chat } from '../global/chatSettingsConfig'
import chatLog from '../global/chat-log'

const ChatStateContext = React.createContext()
const ChatDispatchContext = React.createContext()

export function ChatProvider({ children }) {
  const [state, dispatch] = React.useReducer(ChatReducer, ChatInitialState)
  const [roster, refetchRoster] = useRoster(state.connection)
  const [bookmarks, refetchBookmarks] = useBookmarks()
  const [clearUnreadMessageFromCache] = useClearUnreadMessageFromCache()

  const [chatEnabled, setChatEnabled] = useCache({
    cache: { storeName: 'RTCChat', type: 'local' },
    key: 'enable-chat',
    initialValue: getIsLowBandwidth() ? 'not-enabled' : 'enabled',
  })

  useEffect(() => {
    if (state.rosterNeedsReload) {
      refetchRoster()
    }
    if (state.bookmarksNeedReload) {
      refetchBookmarks()
    }
  }, [refetchBookmarks, refetchRoster, state.bookmarksNeedReload, state.rosterNeedsReload])

  const clearReceivedMessage = useCallback(
    ({ jids }) => {
      jids?.forEach((jid) => {
        clearUnreadMessageFromCache({ jid: jid.toLowerCase() })
        return dispatch({ type: 'CLEAR_UNREAD_MESSAGE', jid: jid.toLowerCase() })
      })
    },
    [clearUnreadMessageFromCache]
  )

  const value = {
    ...state,
    roster,
    refetchRoster,
    bookmarks,
    refetchBookmarks,
    clearReceivedMessage,
    chatEnabled: chatEnabled === 'enabled' && Chat.Settings.SITE_CHAT_ENABLED,
    setChatEnabled,
  }

  return (
    <ChatStateContext.Provider value={value}>
      <ChatDispatchContext.Provider value={dispatch}>{children}</ChatDispatchContext.Provider>
    </ChatStateContext.Provider>
  )
}

export function useChatState() {
  const context = React.useContext(ChatStateContext)
  if (context === undefined) {
    throw new Error('ERROR: useChatState must be used inside of ChatProvider')
  }
  return context
}

export function useChatDispatch() {
  const context = React.useContext(ChatDispatchContext)
  if (context === undefined) {
    throw new Error('ERROR: useChatDispatch must be used inside of ChatProvider')
  }
  return context
}

export function useChatInitialization({ fsUser }) {
  const dispatch = useChatDispatch()
  const { chatEnabled, bookmarks, roster, unreadMessages, clearReceivedMessage } = useChatState()
  const [previousChatEnabled, setPreviousChatEnabled] = useState(false)
  const [isConnecting, setIsConnecting] = React.useState(false)

  const { connect, disconnect, connection } = useChatServerConnection({ fsUser, dispatch })
  useChatServerPing({ connection, dispatch })
  useLoadUnreadMessagesFromCache({ connection, dispatch, bookmarks, roster })

  useEffect(() => {
    if (fsUser?.signedIn && !isConnecting && chatEnabled) {
      setIsConnecting(true)
      setPreviousChatEnabled(true)
      connect()
    }
  }, [connect, fsUser.cisId, fsUser.signedIn, chatEnabled, isConnecting])

  useEffect(() => {
    if (previousChatEnabled && !chatEnabled) {
      setIsConnecting(false)
      setPreviousChatEnabled(false)
      disconnect()
    }
  }, [connection, disconnect, chatEnabled, previousChatEnabled])

  // Brute force way to keep my roster/bookmarks in sync with
  // RTC-1153, RTC-1140, etc.
  useInterval(() => {
    const invalidUnreadMessageJids = []
    unreadMessages?.forEach((jid) => {
      if (jid.includes(Chat.Settings.MUC_DOMAIN)) {
        const hasBookmark =
          bookmarks && bookmarks.find && bookmarks.find((book) => book.jid.toLowerCase() === jid.toLowerCase())
        if (!hasBookmark) {
          invalidUnreadMessageJids.push(jid)
        }
      } else if (jid.includes(Chat.Settings.DOMAIN)) {
        const hasPerson =
          roster && roster.find && roster.find((person) => person.jid.toLowerCase() === jid.toLowerCase())
        if (!hasPerson) {
          invalidUnreadMessageJids.push(jid)
        }
      }
    })

    if (invalidUnreadMessageJids?.length) {
      chatLog(`[Unread]  Found invalid jids in unread messages.`)
      clearReceivedMessage({ jids: invalidUnreadMessageJids })
    }
  }, 10000)
}
